/**
 * OREF (Israeli Home Front Command) siren alert data source.
 * Fetches real-time siren/alert data from the Pikud HaOref system.
 * No API key required. Public government endpoint.
 *
 * Polled every 5 seconds — this is a real-time warning system.
 * Returns empty array when no active alerts (normal state).
 *
 * NOTE: The OREF API is geo-restricted to Israeli IP addresses.
 * This source will return 403 from outside Israel. Use a VPN or
 * Israeli proxy if needed. Circuit breaker handles graceful degradation.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'oref';

/** Map OREF alert categories to severity levels. */
function alertCategoryToSeverity(category: number): Severity {
  // OREF categories: 1=missiles, 2=hostile aircraft, 3=tsunami, etc.
  switch (category) {
    case 1: return 'critical'; // Missiles / rockets
    case 2: return 'critical'; // Hostile aircraft intrusion
    case 3: return 'high';     // Tsunami
    case 4: return 'high';     // Earthquake
    case 5: return 'high';     // Radiological event
    case 6: return 'high';     // Hazardous materials
    case 7: return 'medium';   // Terrorist infiltration
    case 13: return 'critical'; // UAV / drone intrusion
    default: return 'high';
  }
}

function alertCategoryLabel(category: number): string {
  switch (category) {
    case 1: return 'Rocket/Missile Alert';
    case 2: return 'Hostile Aircraft Intrusion';
    case 3: return 'Tsunami Warning';
    case 4: return 'Earthquake Alert';
    case 5: return 'Radiological Hazard';
    case 6: return 'Hazardous Materials';
    case 7: return 'Terrorist Infiltration';
    case 13: return 'UAV/Drone Intrusion';
    default: return 'Security Alert';
  }
}

interface OrefAlert {
  cat: string;    // category number as string
  title: string;  // alert title (Hebrew)
  data: string[]; // list of city/area names
  desc: string;   // description
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const orefSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'OREF (Israeli Sirens)',
    description: 'Israeli Home Front Command real-time siren alerts',
    icon: 'alert-triangle',
    category: 'conflict',
    updateInterval: 5_000, // 5 seconds — real-time alert system
    requiresApiKey: false,
    dataLicense: 'Public',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] OREF siren alert source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/oref`;
      const response = await fetch(url, { signal: AbortSignal.timeout(5_000) });

      if (!response.ok) {
        throw new Error(`OREF API returned ${response.status}`);
      }

      const text = await response.text();

      // OREF returns empty string or empty array when no active alerts
      if (!text || text.trim() === '' || text.trim() === '[]') {
        status.state = 'active';
        status.lastFetch = new Date().toISOString();
        status.eventCount = 0;
        return [];
      }

      let alerts: OrefAlert[];
      try {
        const parsed = JSON.parse(text);
        // Response can be a single object or array
        alerts = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        // Non-JSON response = no active alerts
        status.state = 'active';
        status.lastFetch = new Date().toISOString();
        status.eventCount = 0;
        return [];
      }

      const now = new Date().toISOString();
      const events: NormalizedEvent[] = [];

      for (const alert of alerts) {
        const category = parseInt(alert.cat, 10) || 0;
        const categoryLabel = alertCategoryLabel(category);
        const areas = alert.data ?? [];

        // Create one event per alert, listing all affected areas
        const areaList = areas.join(', ');
        const id = `oref-${djb2Hash(`${alert.cat}-${areaList}-${now.slice(0, 16)}`)}`;

        events.push({
          id,
          source: SOURCE_ID,
          timestamp: now,
          type: 'conflict',
          title: `${categoryLabel}: ${alert.title}`,
          description: areaList || alert.desc,
          severity: alertCategoryToSeverity(category),
          location: {
            lat: 31.5,  // Israel center — individual cities need geocoding
            lng: 34.8,
            country: 'Israel',
            countryCode: 'IL',
            name: areas[0] ?? 'Israel',
          },
          tags: ['oref', 'siren', 'israel', `alert-cat-${category}`],
          raw: {
            category,
            title: alert.title,
            areas,
            description: alert.desc,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = now;
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
